/**
 * ADA Fix: Accordion Focus Trap in Sidebar Menu
 * 
 * Issue: In responsive web design (RWD), the keyboard support provided for the 
 * 2nd level menu accordions is not appropriate and intuitive. The focus is trapped 
 * inside the expanded accordion section. The user has to press ESC to return to 
 * the accordion trigger and press ENTER key to close the accordion.
 * 
 * Recommendation: When user activates the accordion, focus should stay on the 
 * accordion trigger and section should expand. The focusable elements in the 
 * accordion sections should be in the tab order, however the keyboard focus 
 * should not be trapped inside the accordion section.
 * 
 * Reference: https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
 */

(function() {
  'use strict';

  /**
   * Fixes focus trap behavior for accordion panels in sidebar menu
   */
  function fixAccordionFocusTrap(panel) {
    if (!panel) return;

    // Override the focusTrapOptions to disable the focus trap
    Object.defineProperty(panel, 'focusTrapOptions', {
      get: function() {
        return {
          // Prevent focus trap from activating
          onActivate: () => {},
          onDeactivate: () => {},
          // Allow clicks outside without trapping focus
          clickOutsideDeactivates: true,
          allowOutsideClick: true,
          // Don't return focus when deactivating
          returnFocusOnDeactivate: false,
          // Prevent focus from being trapped
          escapeDeactivates: false, // We'll handle ESC ourselves
          // Allow user to tab through normally
          preventScroll: true,
          // Don't set initial focus
          setReturnFocus: false,
          initialFocus: false,
          fallbackFocus: false
        };
      },
      configurable: true
    });

    // Deactivate any existing focus trap
    if (panel._focusTrap) {
      try {
        panel._focusTrap.deactivate();
      } catch (e) {
        console.warn('Could not deactivate focus trap:', e);
      }
      // Reset the focus trap so it gets recreated with new options
      panel._focusTrap = null;
    }
  }

  /**
   * Finds the trigger button for a given panel
   */
  function findTriggerForPanel(panel) {
    if (!panel || !panel.id) return null;
    return document.querySelector(`[aria-controls="${panel.id}"]`);
  }

  /**
   * Handles keyboard events for accordion panels
   */
  function handleAccordionKeyboard(panel) {
    if (!panel) return;

    const trigger = findTriggerForPanel(panel);
    if (!trigger) return;

    // Ensure ARIA attributes are correct
    if (!trigger.hasAttribute('role')) {
      trigger.setAttribute('role', 'button');
    }

    // Update aria-expanded based on panel state
    const updateAriaExpanded = () => {
      const isOpen = panel.hasAttribute('open') || panel.open;
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    updateAriaExpanded();

    // Watch for panel open/close changes
    const observer = new MutationObserver(() => {
      updateAriaExpanded();
    });

    observer.observe(panel, {
      attributes: true,
      attributeFilter: ['open']
    });

    // Handle keyboard events on trigger
    trigger.addEventListener('keydown', function(event) {
      const key = event.key;

      // ENTER or SPACE: Toggle accordion
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        
        // Toggle the panel
        const isCurrentlyOpen = panel.hasAttribute('open') || panel.open;
        
        if (isCurrentlyOpen) {
          panel.open = false;
        } else {
          panel.open = true;
        }

        // Keep focus on trigger
        trigger.focus();
      }
      
      // ESC: Close accordion if open
      else if (key === 'Escape' || key === 'Esc') {
        const isOpen = panel.hasAttribute('open') || panel.open;
        
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          panel.open = false;
          trigger.focus();
        }
      }
    });

    // Handle clicks on trigger - keep focus on trigger
    trigger.addEventListener('click', function(event) {
      // Small delay to ensure the panel state has updated
      setTimeout(() => {
        trigger.focus();
      }, 50);
    });

    // Prevent focus from being moved to panel content on open
    panel.addEventListener('toggle', function(event) {
      // If panel was just opened, ensure focus stays on trigger
      if (panel.open) {
        setTimeout(() => {
          // Only refocus if focus has moved inside the panel
          if (panel.contains(document.activeElement) && document.activeElement !== trigger) {
            // Check if user is using keyboard navigation
            const focusedElement = document.activeElement;
            
            // If the focused element is the panel itself (not a link or button inside),
            // move focus back to trigger
            if (focusedElement === panel || focusedElement.tagName === 'LOESS-COLLAPSIBLE-PANEL') {
              trigger.focus();
            }
          }
        }, 100);
      }
    });
  }

  /**
   * Process all accordion panels in sidebar menu
   */
  function processAccordionPanels() {
    // Find all collapsible panels in the sidebar menu
    const sidebarMenu = document.getElementById('SideBarMenu');
    if (!sidebarMenu) return;

    const panels = sidebarMenu.querySelectorAll('loess-collapsible-panel');
    
    panels.forEach(function(panel) {
      fixAccordionFocusTrap(panel);
      handleAccordionKeyboard(panel);
    });
  }

  /**
   * Handle ESC key globally to close accordions (W3C pattern)
   */
  function handleGlobalEscape() {
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        // Find any open accordion panels in sidebar
        const sidebarMenu = document.getElementById('SideBarMenu');
        if (!sidebarMenu) return;

        const openPanels = sidebarMenu.querySelectorAll('loess-collapsible-panel[open]');
        
        openPanels.forEach(function(panel) {
          const trigger = findTriggerForPanel(panel);
          
          // Close the panel
          panel.open = false;
          
          // Move focus to trigger
          if (trigger) {
            trigger.focus();
          }
        });

        if (openPanels.length > 0) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    });
  }

  /**
   * Initialize on page load
   */
  function initialize() {
    processAccordionPanels();
    handleGlobalEscape();
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Re-run when sidebar menu is opened
  document.addEventListener('expandable-html-element:open', function(event) {
    if (event.target && (event.target.id === 'SideBarMenu' || event.target.id && event.target.id.startsWith('SidebarMenu-'))) {
      // Small delay to ensure content is rendered
      setTimeout(processAccordionPanels, 100);
    }
  });

  // Watch for dynamically added panels
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            if (node.tagName === 'LOESS-COLLAPSIBLE-PANEL' ||
                (node.querySelector && node.querySelector('loess-collapsible-panel'))) {
              setTimeout(processAccordionPanels, 100);
            }
          }
        });
      }
    });
  });

  // Observe the body for changes
  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

})();
